import {
  Box,
  Layout,
  IconArrowForward,
  Typography,
  Button,
  theme,
  GridedLayout,
  GridedMain,
  GridedFullContent,
  GridedFillContent,
} from '@capitalise/ui-design-system';
import ResponsiveSideMenu from '../components/ResponsiveSideMenu';
import BusinessSideMenu from '../components/BusinessSideMenu';
import { useRef, useState, useEffect, cloneElement } from 'react';
import { useAuthContext } from 'HOC/withAuth';
import Header from '../components/Header';
import PropTypes from 'prop-types';
import { QuestionnaireType } from 'enums/questionnaire-type';
import { QuestionaireContextProvider } from '../providers/questionaire';
import useMediaQuery from '../hooks/useMediaQuery';
import Pro from 'assets/svgs/pro.svg';
import { useLocation } from 'react-router-dom';
import { Analytics } from 'utils/Analytics';
import { AnalyticsEventAction, AnalyticsEventCategory } from 'enums/analytics-types';
import { UserType } from 'enums/user-type';
import { BannerContextProvider } from '../providers/upgradeBanner';
import useForceQueryParamRefreshSnapshot from '../hooks/useForceQueryParamRefreshSnapshot';
import { BUSINESS_FORCE_REFRESH_QUERY } from '../shared/types';
import { CountryList } from 'enums/country';
import CountrySpecificBox from 'components/CountrySpecificBox';
import { PaymentOrigin, useRedirectTrackingPaymentOrigin } from '@capitalise/common';
import BusinessTabBarNavigation from '../components/BusinessTabBarNavigation/BusinessTabBarNavigation';
import useScrollDirection from '../hooks/useScrollDirection';
import { use100vh } from 'react-div-100vh';

const BusinessLayout = ({ children, hideSideMenu, injectOrigin }) => {
  const { apiKey, isPro, user: auth } = useAuthContext();
  const { pathname } = useLocation();
  const isFundingPage = pathname === '/business/funding';
  useForceQueryParamRefreshSnapshot(auth?.company?.company_id, BUSINESS_FORCE_REFRESH_QUERY);
  const { isMobile } = useMediaQuery();
  const content = useRef(null);
  const [open, setOpen] = useState(false);
  const { redirectToPaymentFlow } = useRedirectTrackingPaymentOrigin();
  const height = use100vh();

  const countryCode = auth?.locale?.countryCode;
  const isGB = countryCode === CountryList.GREAT_BRITAIN;
  const { direction } = useScrollDirection(content);

  useEffect(() => {
    if (hideSideMenu) {
      setOpen(false);
    }
  }, [hideSideMenu]);

  return (
    <QuestionaireContextProvider
      apiKey={apiKey}
      defaultType={QuestionnaireType.businessWorkingCapital}
      entityId={auth?.company?.company_id}
    >
      <BannerContextProvider>
        <GridedLayout height={`${height}px`}>
          <Header
            menuClickCallback={setOpen}
            isOpen={open}
            contentRef={content}
            injectOrigin={injectOrigin}
          />

          <GridedMain>
            <GridedFullContent>
              <ResponsiveSideMenu open={open} onClose={() => setOpen(!open)}>
                <BusinessSideMenu onClose={() => setOpen(!open)} />
              </ResponsiveSideMenu>

              <GridedFillContent ref={content}>
                <CountrySpecificBox country={CountryList.GREAT_BRITAIN}>
                  {!isPro && isMobile && !isFundingPage && (
                    <Box
                      role="presentation"
                      cursor="pointer"
                      onClick={() => {
                        Analytics.track(
                          AnalyticsEventCategory.business,
                          AnalyticsEventAction.upgradeStart,
                          {
                            extraParams: {
                              userType: UserType.business,
                              companyType: auth?.company?.type,
                              membership: auth?.membership?.level?.name,
                              upgradeOrigin: 'page header',
                            },
                          },
                        );
                        redirectToPaymentFlow(PaymentOrigin.CREDIT_PROFILE);
                      }}
                      width="100%"
                      backgroundColor="green-500"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      paddingX="3xsmall"
                      paddingY="4xsmall"
                      marginTop="tiny"
                    >
                      <Box minWidth={theme.sizes['small']} height={theme.sizes['3xsmall']}>
                        <Pro />
                      </Box>
                      <Box marginX="3xsmall">
                        <Typography size="body-16" color="white">
                          Access your full Experian credit score with Capitalise Pro
                        </Typography>
                      </Box>
                      <Box>
                        <Button variant="text" baseColor="white" rightIcon={<IconArrowForward />}>
                          Get Pro
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CountrySpecificBox>
                {injectOrigin ? cloneElement(children, { fromBusinessLayout: true }) : children}
              </GridedFillContent>
            </GridedFullContent>
          </GridedMain>
          {isGB && isMobile && (
            <BusinessTabBarNavigation show={direction === 'BOTTOM' || !direction} />
          )}
        </GridedLayout>
      </BannerContextProvider>
    </QuestionaireContextProvider>
  );
};

BusinessLayout.propTypes = {
  children: PropTypes.node.isRequired,
  hideSideMenu: PropTypes.bool,
  injectOrigin: PropTypes.bool,
};

const withBusinessLayout = (Component, options) => {
  return (props) => (
    <Layout>
      <BusinessLayout {...(options || {})}>
        <Component {...props} />
      </BusinessLayout>
    </Layout>
  );
};

export default withBusinessLayout;
